import React, { useState, useMemo, useRef, useEffect } from 'react';
import Hero from '@components/Hero';
import Controls from '@components/Controls';
import Content from '@components/Content';
import DebugMenu from '@components/DebugMenu';
import useLoanDetails from '@hooks/useLoanDetails';
import useRackRates from '@hooks/useRackRates';
import useProductTypes from '@hooks/useProductTypes';
import useScenario from '@hooks/useScenario';
import useWindowResizing from '@hooks/useWindowResizing';
import { initProps } from '@helpers';
import classNames from 'classnames';

export default function App(initialProps) {
  //////////////////////////
  // Server Props
  //////////////////////////

  const {
    productTypes: defaultProductTypes,
    cachedRates: initialRateData,
    rackRates: initialRackRates,
    defaultScenarios,
    configuration,
    requestPhases,
    validations,
    ctas,
    faqs,
    debugErrorCodes
  } = useMemo(() => {
    return initProps(initialProps);
  }, [initialProps]);

  //////////////////////////
  // Global State
  //////////////////////////

  const { loanDetails, setLoanDetails } = useLoanDetails(defaultScenarios);

  const {
    productTypes,
    hasEnabledProductTypes,
    toggleProductCategory,
    toggleProductType
  } = useProductTypes(defaultProductTypes);

  const {
    showRackRates,
    rackRateGroups,
    rackRateLoanPurpose,
    rackRateLoanType,
    setRackRateLoanPurpose,
    setRackRateLoanType,
    fetchRackRateGroups,
    error: rackRatesError,
    isLoading: rackRatesAreLoading
  } = useRackRates(
    configuration.showRackRates,
    initialRackRates,
    productTypes,
    defaultScenarios[defaultScenarios.defaultLoanPurpose]
  );

  const {
    rates,
    effectiveDate,
    errors,
    isLoading,
    isLoadingCategory,
    canLoadMore,
    hasInitialData,
    loadMore,
    prefetchScenario,
    testApiError
  } = useScenario(
    loanDetails,
    requestPhases,
    productTypes,
    initialRateData,
    defaultScenarios,
    showRackRates
  );

  //////////////////////////
  // User Interface
  //////////////////////////

  const [inputsAreOpen, setInputsAreOpen] = useState(false);
  const [inputsRect, setInputsRect] = useState({
    width: 0,
    height: 0,
    left: 0,
    controlsTop: 0,
    controlsWrapperTop: 0
  });

  useWindowResizing('calculator-resizing');

  useEffect(() => {
    if (configuration.enableDebugMode) {
      document.body.classList.add('debug');
    }
  }, [configuration.enableDebugMode]);

  return (
    <div
      id="mortgage-rate-calculator"
      className={classNames({
        'inputs-open': inputsAreOpen,
        'disable-dynamic-rates': showRackRates,
        debug: configuration.enableDebugMode
      })}
    >
      <TopWrapper
        loanDetails={loanDetails}
        inputsAreOpen={inputsAreOpen}
        defaultLoanPurpose={defaultScenarios.defaultLoanPurpose}
        validations={validations}
        errors={errors}
        showRackRates={showRackRates}
        rackRateLoanPurpose={rackRateLoanPurpose}
        rackRateLoanType={rackRateLoanType}
        setRackRateLoanPurpose={setRackRateLoanPurpose}
        setRackRateLoanType={setRackRateLoanType}
        setLoanDetails={setLoanDetails}
        setInputsRect={setInputsRect}
        setInputsAreOpen={setInputsAreOpen}
        prefetchScenario={prefetchScenario}
      />
      <Content
        loanDetails={loanDetails}
        inputsRect={inputsRect}
        inputsAreOpen={inputsAreOpen}
        productTypes={productTypes}
        hasEnabledProductTypes={hasEnabledProductTypes}
        rates={rates}
        effectiveDate={effectiveDate}
        isLoading={isLoading}
        isLoadingCategory={isLoadingCategory}
        canLoadMore={canLoadMore}
        hasInitialData={hasInitialData}
        errors={errors}
        showRackRates={showRackRates}
        rackRateGroups={rackRateGroups}
        rackRateLoanPurpose={rackRateLoanPurpose}
        rackRateLoanType={rackRateLoanType}
        rackRatesAreLoading={rackRatesAreLoading}
        rackRatesError={rackRatesError}
        ctas={ctas}
        faqs={faqs}
        toggleProductCategory={toggleProductCategory}
        toggleProductType={toggleProductType}
        loadMore={loadMore}
        fetchRackRateGroups={fetchRackRateGroups}
      />
      {configuration.enableDebugMode && (
        <DebugMenu
          debugErrorCodes={debugErrorCodes}
          showRackRates={showRackRates}
          testApiError={testApiError}
        />
      )}
    </div>
  );
}

const TopWrapper = React.memo(
  ({
    loanDetails,
    setLoanDetails,
    inputsAreOpen,
    defaultLoanPurpose,
    validations,
    errors,
    showRackRates,
    rackRateLoanPurpose,
    rackRateLoanType,
    setRackRateLoanPurpose,
    setRackRateLoanType,
    setInputsRect,
    setInputsAreOpen,
    prefetchScenario
  }) => {
    const ref = useRef();

    return (
      <div id="calculator-top" ref={ref}>
        <Hero topWrapper={ref} />
        <Controls
          state={loanDetails}
          setState={setLoanDetails}
          inputsAreOpen={inputsAreOpen}
          defaultLoanPurpose={defaultLoanPurpose}
          validations={validations}
          apiErrors={errors}
          showRackRates={showRackRates}
          rackRateLoanPurpose={rackRateLoanPurpose}
          rackRateLoanType={rackRateLoanType}
          setRackRateLoanPurpose={setRackRateLoanPurpose}
          setRackRateLoanType={setRackRateLoanType}
          setInputsRect={setInputsRect}
          setInputsAreOpen={setInputsAreOpen}
          prefetchScenario={prefetchScenario}
        />
      </div>
    );
  }
);
